import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import InfoTooltip from 'components/InfoTooltip';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'store';
import { useUpdateInvoiceDebtNotesMutation } from 'store/api/invoices';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { InvoiceData, InvoiceForm, InvoiceStatusType } from 'types/invoices';
import { formatTimeOfDay } from 'utils/date';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { ReasonTooltip } from '../../components/ReasonTooltip';

type Props = {
    invoice: InvoiceData;
    /**
     * The new design's card, which saves on its Save: the fields wait for it, who set a value and when shows under
     * it, and the Receivability history opens beside the page.
     */
    isInCard?: boolean;
};

/** Saves the debt collection fields as the invoice's form holds them. */
export function useSaveDebtNotes(invoiceId: number) {
    const { getValues } = useFormContext<InvoiceForm>();
    const [updateInvoiceDebtNotes] = useUpdateInvoiceDebtNotesMutation();
    return useCallback(() => {
        const debtNotes = getValues('debtNotes');
        const nextDebtCollectionActionType = getValues('nextDebtCollectionActionType');
        const receivabilityType = getValues('receivabilityType');
        const penaltyAmount = getValues('penaltyAmount');
        const interestPaidOn = getValues('interestPaidOn');
        const nextReminderDate = getValues('nextReminderDate');
        const interestRate = getValues('interestRate');

        return updateInvoiceDebtNotes({
            invoiceId,
            debtNotes: debtNotes ?? '',
            nextDebtCollectionActionTypeCode: nextDebtCollectionActionType?.value ?? null,
            receivabilityTypeCode: receivabilityType?.value ?? null,
            penaltyAmount: penaltyAmount ? parseFloat(penaltyAmount) : null,
            interestPaidOn: interestPaidOn ?? null,
            nextReminderDate: nextReminderDate ?? null,
            interestRate: interestRate ? parseFloat(interestRate) : null,
        });
    }, [getValues, invoiceId, updateInvoiceDebtNotes]);
}

/** Who set a debt collection value and when, in one line; nothing until someone has. */
export function useDebtCollectionAuditInfo() {
    const { formatMessage } = useIntl();
    const dateFormat = useEffectiveDateFormat();
    // The new design writes the day in the company's format and the time in its locale, in one message.
    return (date?: Date, user?: { name: string }) => {
        if (!date || !user) return null;
        if (isNewDesignCopy()) {
            return formatMessage(
                { id: 'invoices.debt-collection-set-by' },
                { name: user.name, date: format(new Date(date), dateFormat), time: formatTimeOfDay(date) }
            );
        }
        const formattedDate = new Date(date).toLocaleDateString();
        const formattedTime = new Date(date).toLocaleTimeString();
        return `Set by ${user.name} on ${formattedDate} at ${formattedTime}`;
    };
}

/** A value's "set by" line, under its field. */
function SetByLine({ children }: { children: ReactNode }) {
    return children ? <p className="text-muted-foreground text-caption mt-1">{children}</p> : null;
}

const DebtCollectionCard = ({ invoice, isInCard = false }: Props) => {
    const { control, setValue } = useFormContext<InvoiceForm>();
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { formatMessage } = useIntl();
    const isPostponed = invoice?.invoiceStatusTypeCode === InvoiceStatusType.POSTPONED;
    const hasPermissionToUpdateDebtNotes = useHasPermissions(['UPDATE.INVOICES.BILLING_NOTES']);
    // In the card its own Save is a request too, and the fields stay put while it runs.
    const isDisabled = !hasPermissionToUpdateDebtNotes || (buttonsDisabled && !isInCard) || isPostponed;

    const saveDebtNotes = useSaveDebtNotes(invoice.id);

    const isInvoiceInProcessing =
        invoice.invoiceStatusTypeCode === InvoiceStatusType.DRAFT ||
        invoice.invoiceStatusTypeCode === InvoiceStatusType.REVIEWED ||
        invoice.invoiceStatusTypeCode === InvoiceStatusType.APPROVED ||
        invoice.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED;

    const handleUpdateDebtNotes = (): void => {
        if (!isInCard) {
            void saveDebtNotes();
        }
    };

    const { classifiers } = useClassifiers({
        codes: ['NEXT_DEBT_COLLECTION_ACTION_TYPE', 'RECEIVABILITY_TYPE'],
        autoSort: true,
    });

    const debtCollectionActionOptions = getValidOptionsFromClassifier(
        classifiers['NEXT_DEBT_COLLECTION_ACTION_TYPE'],
        false
    ) as { label: string; value: string }[];

    const receivabilityOptions = getValidOptionsFromClassifier(classifiers['RECEIVABILITY_TYPE']) as {
        label: string;
        value: string;
    }[];

    const formatAuditInfo = useDebtCollectionAuditInfo();

    const nextDebtCollectionAuditInfo = formatAuditInfo(
        invoice.nextDebtCollectionActionSetAt,
        invoice.nextDebtCollectionActionSetBy
    );

    const receivabilityAuditInfo = formatAuditInfo(invoice.receivabilitySetAt, invoice.receivabilitySetBy);
    const historyButton = (
        <DisplayHistoryButton
            entityType={HistoryEntityType.INVOICE}
            entityId={`${invoice.id}`}
            fieldType={HistoryFieldType.RECEIVABILITY_TYPE_CODE}
            fieldName={formatMessage({ id: 'invoices.receivability' })}
            activity={formatMessage({ id: 'updating' })}
            detached={isInCard ? receivabilityHistorySheet(invoice) : undefined}
            plain={isInCard}
        />
    );

    return (
        <div className="flex flex-col gap-4">
            {/* The new design's card carries the anchor itself, in read and in edit. */}
            <div id={isInCard ? undefined : 'debt-notes'}>
                <TextAreaInput
                    control={control}
                    name="debtNotes"
                    label="invoices.debt-collection-notes"
                    readonly={isDisabled}
                    onBlur={handleUpdateDebtNotes}
                    enableAutofill
                    minRows={3}
                    maxRows={3}
                    // A section draws one line to start with; the card's notes start three lines tall.
                    wrapperClassName={isInCard ? '[&_textarea]:min-h-[84px]' : undefined}
                />
            </div>

            <div className="flex flex-row items-center gap-4">
                <div className="flex-1">
                    <DateInput
                        label="invoices.next-reminder-date"
                        control={control}
                        name="nextReminderDate"
                        readonly={isDisabled}
                        onChange={handleUpdateDebtNotes}
                        fullWidth
                        disabled={isInvoiceInProcessing}
                    />
                </div>

                <ReasonTooltip reason={isInCard ? null : receivabilityAuditInfo}>
                    <div className="flex-1">
                        <SelectInput
                            label="invoices.receivability"
                            control={control}
                            name="receivabilityType"
                            options={receivabilityOptions ?? []}
                            getOptionIdentifier="value"
                            getOptionLabel="label"
                            setValue={setValue}
                            readonly={isDisabled}
                            onChange={handleUpdateDebtNotes}
                            // The card's history sits beside the label's info, where a section's right slot puts it
                            // at the row's far end.
                            tooltip={
                                isInCard ? (
                                    <>
                                        <InfoTooltip text="invoices.tooltips.receivability" />
                                        {historyButton}
                                    </>
                                ) : (
                                    <InfoTooltip text="invoices.tooltips.receivability" />
                                )
                            }
                            rightLabelComponent={isInCard ? undefined : historyButton}
                            required
                            clearable={false}
                        />
                        {isInCard ? <SetByLine>{receivabilityAuditInfo}</SetByLine> : null}
                    </div>
                </ReasonTooltip>
            </div>

            <div className="flex flex-row items-center gap-4">
                <div className="flex-1">
                    <DecimalInput
                        label="invoices.penalty-amount"
                        control={control}
                        name="penaltyAmount"
                        readonly={isDisabled}
                        onBlur={handleUpdateDebtNotes}
                        tooltip={<InfoTooltip text="invoices.tooltips.penalty-amount" />}
                    />
                </div>

                <div className="flex-1">
                    <PercentageInput
                        label="invoices.interest-rate"
                        control={control}
                        name="interestRate"
                        readonly={isDisabled}
                        onBlur={handleUpdateDebtNotes}
                        decimalPlaces={2}
                    />
                </div>
            </div>

            <div className="flex flex-row items-center gap-4">
                <div className="flex-1">
                    <DateInput
                        label="invoices.interest-paid-on"
                        control={control}
                        name="interestPaidOn"
                        readonly={isDisabled}
                        onChange={handleUpdateDebtNotes}
                        fullWidth
                    />
                </div>

                <div className="flex-1">
                    <ReasonTooltip reason={isInCard ? null : nextDebtCollectionAuditInfo}>
                        <div>
                            <SelectInput
                                label="invoices.next-debt-collection-action"
                                control={control}
                                name="nextDebtCollectionActionType"
                                options={debtCollectionActionOptions ?? []}
                                getOptionIdentifier="value"
                                getOptionLabel="label"
                                setValue={setValue}
                                readonly={isDisabled}
                                onChange={handleUpdateDebtNotes}
                                tooltip={<InfoTooltip text="invoices.tooltips.next-debt-collection-action" />}
                            />
                            {isInCard ? <SetByLine>{nextDebtCollectionAuditInfo}</SetByLine> : null}
                        </div>
                    </ReasonTooltip>
                </div>
            </div>
        </div>
    );
};

/** The Receivability history as the new design opens it: a side sheet naming the invoice and the card. */
export const receivabilityHistorySheet = (invoice: InvoiceData) => ({
    subtitle: t('invoices.debt-collection-history-subtitle', { invoiceNumber: invoice.invoiceNumber ?? '' }),
    description: t('invoices.receivability-history-description'),
});

export default DebtCollectionCard;
